import { ReactComponent as Trash } from "../../../assets/trash.svg";
import { ReactComponent as Edit } from "../../../assets/pencil-paper.svg";
import { ReactComponent as Image } from "../../../assets/image.svg";
import styles from "../styles.module.scss";
import { useAtom, useAtomValue } from "jotai";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import {
  createUpdateInstitution,
  createUpdateUploadNysc,
  deleteInstitution,
  deleteNyscUpload,
  getInstitutionByApplicantId,
  getUploadNyscByApplicantId,
} from "../../../utils/request";
import { pageIndexAtom, userAtom } from "../../../utils/store";
import { App, Spin } from "antd";
import InstitutionForm from "./form";
import { format } from "date-fns";
import { Form, Formik } from "formik";
import { Button, Upload } from "../../../custom";
import OnboardingButtonGroup from "../onboardingButtonGroup";
import {
  isPdfOrImageFile,
  PDF_IMAGE_ACCEPT,
  PDF_IMAGE_FORMAT_LABELS,
} from "../../../utils/fileValidation";

const Institution = () => {
  const user = useAtomValue(userAtom);
  const [pageIndex, setPageIndex] = useAtom(pageIndexAtom);

  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [activeInstitutionId, setActiveInstitutionId] = useState(0);
  const [nyscUrl, setNyscUrl] = useState("");

  const queryClient = useQueryClient();

  const { notification } = App.useApp();

  const queries = useQueries({
    queries: [
      {
        queryKey: ["get-institution"],
        queryFn: () => getInstitutionByApplicantId(user?.applicantId ?? ""),
      },
      {
        queryKey: ["get-nysc-upload", user?.applicantId],
        queryFn: () => getUploadNyscByApplicantId(user?.applicantId ?? ""),
        enabled: !!user?.applicantId,
      },
    ],
  });

  const institutionQuery = queries[0];
  const nyscUploadQuery = queries[1];

  const isInstitutionLoading = institutionQuery?.isLoading;
  const isNyscLoading = nyscUploadQuery?.isLoading;

  const isInstitutionError = institutionQuery?.isError;
  const institutionError = institutionQuery?.error;

  const nyscUploadData = useMemo(() => {
    return nyscUploadQuery?.data?.data ?? ({} as NyscUpload);
  }, [nyscUploadQuery?.data?.data]);

  useEffect(() => {
    setNyscUrl(nyscUploadData?.nyscUrl);
  }, [nyscUploadData]);

  const institutionData = useMemo(() => {
    const data = institutionQuery?.data?.data;
    return Array.isArray(data) ? data : [];
  }, [institutionQuery?.data?.data]);

  const [formVisible, setFormVisible] = useState(institutionData?.length === 0);

  useEffect(() => {
    if (institutionData?.length === 0) {
      setFormVisible(true);
    }
  }, [institutionData]);

  const createUpdateInstitutionMutation = useMutation({
    mutationFn: createUpdateInstitution,
  });

  const createUpdateUploadNyscMutation = useMutation({
    mutationFn: createUpdateUploadNysc,
  });

  const deleteInstitutionMutation = useMutation({
    mutationFn: deleteInstitution,
  });

  const deleteNyscUploadMutation = useMutation({
    mutationFn: deleteNyscUpload,
  });

  const formatDate = (date: string) => {
    const convertToDateObject = new Date(date);
    const formattedDate = format(convertToDateObject, "yyyy-MM-dd");

    return formattedDate;
  };

  const institution = institutionData?.map((item) => {
    return {
      institutionName: item?.institutionName,
      discipline: item?.discipline,
      cgpa: item?.cgpa,
      startDate: formatDate(item?.startDate),
      endDate: formatDate(item?.endDate),
      upload: null,
    };
  });

  const handleCreateUpdateInstitution = async (values: Institution) => {
    const formData = new FormData();

    formData.append("Id", activeInstitutionId.toString());
    formData.append("ApplicantId", user?.applicantId ?? "");
    formData.append("InstitutionName", values.institutionName);
    formData.append("Discipline", values.discipline);
    formData.append("CGPA", values.cgpa);
    formData.append("StartDate", values.startDate);
    formData.append("EndDate", values.endDate);
    if (values.upload) {
      formData.append("Certificate", values.upload);
    }

    try {
      await createUpdateInstitutionMutation.mutateAsync(formData, {
        onSuccess: (data) => {
          notification.success({
            message: "Success",
            description: data?.message,
          });

          setFormVisible((prevState) => !prevState);

          queryClient.refetchQueries({ queryKey: ["get-institution"] });
        },
      });
    } catch (error: any) {
      notification.error({
        message: "Error",
        description: error?.response?.data?.message,
      });
    }
  };

  const handleDeleteInstitution = async (id: number) => {
    try {
      await deleteInstitutionMutation.mutateAsync(id, {
        onSuccess: (data) => {
          notification.success({
            message: "Success",
            description: data?.message,
          });

          queryClient.refetchQueries({ queryKey: ["get-institution"] });
        },
      });
    } catch (error: any) {
      notification.error({
        message: "Error",
        description: error?.response?.data?.message,
      });
    }
  };

  const handleDeleteNyscUpload = async () => {
    if (!nyscUploadData?.id) {
      notification.error({
        message: "Error",
        description: "Unable to identify the uploaded NYSC document.",
      });
      return;
    }

    try {
      await deleteNyscUploadMutation.mutateAsync(nyscUploadData.id, {
        onSuccess: (data) => {
          notification.success({
            message: "Success",
            description: data?.message,
          });
          setNyscUrl("");
          queryClient.setQueryData(
            ["get-nysc-upload", user?.applicantId],
            (previousData: NyscUploadResponse | undefined) => ({
              ...previousData,
              data: {} as NyscUpload,
            }),
          );
          queryClient.invalidateQueries({
            queryKey: ["get-nysc-upload", user?.applicantId],
          });
        },
      });
    } catch (error: any) {
      notification.error({
        message: "Error",
        description: error?.response?.data?.message,
      });
    }
  };

  const handleCreateUpdateUploadNysc = async (values: NYSCUpload) => {
    const formData = new FormData();

    formData.append("Id", nyscUploadData?.id?.toString() ?? "0");
    formData.append("ApplicantId", user?.applicantId ?? "");
    if (values.nyscUpload) {
      formData.append("Nysc", values.nyscUpload);
    }

    try {
      await createUpdateUploadNyscMutation.mutateAsync(formData, {
        onSuccess: (data) => {
          notification.success({
            message: "Success",
            description: data?.message,
          });

          queryClient.invalidateQueries({
            queryKey: ["get-nysc-upload", user?.applicantId],
          });
        },
      });
    } catch (error: any) {
      notification.error({
        message: "Error",
        description: error?.response?.data?.message,
      });
    }
  };

  const handleEdit = (index: number, id: number) => {
    setEditIndex((prevIndex) => (prevIndex === index ? null : index));
    setActiveInstitutionId(id);
    setFormVisible(false);
  };

  const handleAddMore = () => {
    setFormVisible(true);
    setEditIndex(null);
    setActiveInstitutionId(0);
  };

  const handleRemoveImageUrl = () => {
    handleDeleteNyscUpload();
  };

  const handlePrevClick = () => {
    setPageIndex(pageIndex > 1 ? pageIndex - 1 : pageIndex);
  };

  const handleNextClick = () => {
    if (institutionData?.length === 0) {
      notification.error({
        message: "Error",
        description: "Please completely fill the form to proceed",
      });
    } else {
      setPageIndex(pageIndex + 1);
    }
  };

  if (isInstitutionLoading || isNyscLoading) {
    return <Spin size="large" />;
  }

  if (isInstitutionError) {
    return <div>{institutionError?.message}</div>;
  }

  return (
    <>
      <section className={styles.section}>
        <h5>Institutions</h5>
        <p>Input details of your attended institutions (if any)</p>
      </section>

      <hr />

      <div className={styles.docSection}>
        {institutionData?.map((item, index) => (
          <div key={item?.id}>
            <section className="space-between">
              <div>
                <h5>{item?.institutionName}</h5>
                <small>
                  {formatDate(item?.startDate)} - {formatDate(item?.endDate)}
                </small>
              </div>

              <div className="small-gap">
                <button
                  type="button"
                  className={styles.transparentBtn}
                  onClick={() => handleEdit(index, item?.id)}
                >
                  <Edit />
                </button>

                <button
                  type="button"
                  className={styles.transparentBtn}
                  onClick={() => handleDeleteInstitution(item?.id)}
                >
                  <Trash />
                </button>
              </div>
            </section>

            {editIndex === index && (
              <InstitutionForm
                editIndex={editIndex}
                institution={institution}
                buttonText="Update"
                certificateUrl={item?.certificateUrl}
                isLoading={createUpdateInstitutionMutation?.isPending}
                onSubmit={handleCreateUpdateInstitution}
              />
            )}
          </div>
        ))}

        {institutionData?.length > 1 && <hr />}

        {formVisible && (
          <InstitutionForm
            editIndex={editIndex}
            institution={institution}
            buttonText="Create"
            isLoading={createUpdateInstitutionMutation?.isPending}
            onSubmit={handleCreateUpdateInstitution}
          />
        )}

        {!formVisible && (
          <button onClick={handleAddMore} className={styles.addQualification}>
            <div>
              <span>+</span>
            </div>

            <span>
              <b>Add Institution</b>
            </span>
          </button>
        )}

        <Formik<NYSCUpload>
          initialValues={{ nyscUpload: null }}
          onSubmit={(values) => {
            handleCreateUpdateUploadNysc(values);
          }}
        >
          {({ setFieldValue, values }) => {
            return (
              <Form>
                {nyscUrl ? (
                  <div className="small-gap">
                    <Image />
                    <span>{nyscUrl}</span>
                    <Button
                      type="button"
                      onClick={handleRemoveImageUrl}
                      text={
                        deleteNyscUploadMutation.isPending ? "Deleting..." : "X"
                      }
                      className={styles.cancel}
                      disabled={deleteNyscUploadMutation.isPending}
                    />
                  </div>
                ) : (
                  <div className={styles.nyscUpload}>
                    <Upload
                      name="nyscUpload"
                      label="Upload NYSC Certificate"
                      fileName={values.nyscUpload ? values.nyscUpload.name : ""}
                      accept={PDF_IMAGE_ACCEPT}
                      allowedFormats={PDF_IMAGE_FORMAT_LABELS}
                      onChange={(e) => {
                        const file = e.target.files;
                        if (file?.[0]) {
                          if (!isPdfOrImageFile(file[0])) {
                            notification.error({
                              message: "Invalid file type",
                              description: "Please upload a PDF or image file.",
                            });
                            setFieldValue("nyscUpload", null);
                            return;
                          }

                          setFieldValue("nyscUpload", file[0]);
                        }
                      }}
                    />

                    <Button
                      type="submit"
                      disabled={createUpdateUploadNyscMutation.isPending}
                      isLoading={createUpdateUploadNyscMutation.isPending}
                      text="Upload"
                    />
                  </div>
                )}
              </Form>
            );
          }}
        </Formik>

        <OnboardingButtonGroup
          onClick={handlePrevClick}
          onNextClick={handleNextClick}
        />
      </div>
    </>
  );
};

export default Institution;
